// src/scenes/MapScene.js
// ─────────────────────────────────────────────────────────────────────────────
// Level-selection map — suporta todas as 5 regiões dinamicamente.
//
// Layout (540 × 960)
//   [0  – 60]   Top bar   : title | BESTIARY btn | RESET btn
//   [60 – 855]  Node map  : 30 nodes, zigzag, themed
//   [855 – 960] Bottom bar: fire monster | region info | next-world btn
//
// Depth budget
//   0-9   background (gradient, bg image, nebula, stars, orb)
//  20-22  node lines + circles + labels
//  50-55  top bar + bottom bar panels
//  500+   modal overlay
// ─────────────────────────────────────────────────────────────────────────────

import { LevelStore, REGIONS_ORDER }          from "../game/levelStore.js";
import { RegionThemes, hexToPhaser }           from "../data/regionThemes.js";
import { createBackground }                    from "../vfx/background.js";
import { PaymentService }                      from "../services/PaymentService.js";
import { I18n }                                from "../game/I18n.js";
import { shouldShowNoxysPromo, showNoxysPromo } from "../ui/noxysPromo.js";

// Vite asset imports (resolved to proper URLs at build time)
import fireImg from "../assets/monsters/fire/fire.png";

// ── Metadados por região ──────────────────────────────────────────────────────
// nextRegionId is resolved at runtime; nextName derived from RegionThemes
const REGION_META = {
  volcano: { labelKey: "region_label", labelN: 1, monster: "Pyron",   nextRegion: "storm"   },
  storm:   { labelKey: "region_label", labelN: 2, monster: "Voltrix", nextRegion: "ocean"   },
  ocean:   { labelKey: "region_label", labelN: 3, monster: "Aquyra",  nextRegion: "earth"   },
  earth:   { labelKey: "region_label", labelN: 4, monster: "Sylrana", nextRegion: "psychic" },
  psychic: { labelKey: "region_label", labelN: 5, monster: "Athena",  nextRegion: null      },
};

export default class MapScene extends Phaser.Scene {
  constructor() {
    super("MapScene");
  }

  // Recebe regionId do LevelScene ou do botão "next world"
  init(data) {
    const saved = LevelStore.loadProgress().currentRegionId || "volcano";
    this.regionId = data?.regionId || saved;
    this._fromVictory = !!data?.fromVictory;

    // Garante que a região pedida está de facto desbloqueada
    if (!LevelStore.isRegionUnlocked(this.regionId)) {
      this.regionId = "volcano";
    }

    // Persiste para que a próxima abertura do mapa volte aqui
    LevelStore.setCurrentRegion(this.regionId);
  }

  preload() {
    this.load.image("map-fire", fireImg);


  }

  create() {
    this.progress = LevelStore.loadProgress();
    this.theme    = RegionThemes[this.regionId] || RegionThemes.volcano;

    const W = this.scale.width;   // 540
    const H = this.scale.height;  // 960

    // ── 1. Themed background
    createBackground(this, this.regionId, { noBgImage: true });

    // ── 2. Top bar
    this._drawTopBar(W);

    // ── 3. Level nodes + connector lines
    this._drawNodes(W, H);

    // ── 4. Bottom bar
    this._drawBottomBar(W, H);

    // ── 5. Dev panel — stripped by Vite in production builds
    if (import.meta.env.DEV) {
      this.input.keyboard.on('keydown-D', () => this._openDevPanel());
    }

    // ── 6. NOXYS cross-promo — show after victory, 1-3 times per region
    if (this._fromVictory && shouldShowNoxysPromo(this.regionId)) {
      this.time.delayedCall(800, () => showNoxysPromo(this, this.regionId));
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Top bar — dark panel with title + two pill buttons
  // ─────────────────────────────────────────────────────────────────────────
  _drawTopBar(W) {
    const t    = this.theme;
    const D    = 50;
    const meta = REGION_META[this.regionId] || REGION_META.volcano;
    const name = t.name || I18n.t('region_unknown');
    const label = I18n.t(meta.labelKey, { n: meta.labelN });

    this.add.rectangle(W / 2, 30, W, 60, 0x08090f, 0.94)
      .setDepth(D);

    this.add.rectangle(0, 59, W, 2, hexToPhaser(t.primary), 0.55)
      .setOrigin(0, 0)
      .setDepth(D + 1);

    this.add.text(W / 2, 30, `${name.toUpperCase()}  —  ${label.toUpperCase()}`, {
      fontFamily: "system-ui, Arial",
      fontSize:   "13px",
      color:      t.starColor,
      fontStyle:  "bold",
    }).setOrigin(0.5).setDepth(D + 2);

    this._makeButton(52, 30, I18n.t('btn_collection'), t.primary, 90, D + 2,
      () => this.scene.start("CollectionScene", { from: "MapScene" }));

    this._makeButton(154, 30, I18n.t('btn_scores'), "#fbbf24", 76, D + 2,
      () => this.scene.start("LeaderboardScene", { from: "MapScene" }));

    // Loja — só aparece se o jogo ainda não foi desbloqueado
    if (!PaymentService.isUnlocked()) {
      this._makeButton(246, 30, I18n.t('btn_store'), "#a855f7", 78, D + 2,
        () => this.scene.start("StoreScene", { returnTo: "MapScene", regionId: this.regionId }));
    }

    // Badge de Treinador — nível persistido entre sessões
    const trainerLv = LevelStore.getTrainerLevel();
    this.add.text(14, 50, I18n.t('trainer_level', { n: trainerLv }), {
      fontFamily: "system-ui, Arial",
      fontSize:   "8px",
      color:      "#f59e0b",
    }).setDepth(D + 2);

    this._makeButton(W - 46, 30, I18n.t('btn_reset'), "#ef4444", 70, D + 2, () => {
      LevelStore.resetProgress();
      this.scene.restart();
    });

    this._makeButton(W - 46 - 78, 30, "⚙", "#94a3b8", 36, D + 2,
      () => this.scene.launch("OptionsScene"));

    // [DEV] button — present only during `vite dev`, removed by `vite build`
    if (import.meta.env.DEV) {
      this._makeButton(W - 46 - 78 - 46, 30, "DEV", "#a855f7", 54, D + 2,
        () => this._openDevPanel());
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Pill button helper
  // ─────────────────────────────────────────────────────────────────────────
  _makeButton(x, y, label, color, btnW, depth, callback) {
    const colorInt = hexToPhaser(color);

    const bg = this.add.rectangle(x, y, btnW, 26, 0x0f172a, 0.92)
      .setStrokeStyle(1.5, colorInt, 0.85)
      .setDepth(depth)
      .setInteractive({ useHandCursor: true });

    const txt = this.add.text(x, y, label, {
      fontFamily: "system-ui, Arial",
      fontSize:   "11px",
      color,
      fontStyle:  "bold",
    }).setOrigin(0.5).setDepth(depth + 1);

    bg.on("pointerover",  () => bg.setFillStyle(colorInt, 0.22));
    bg.on("pointerout",   () => bg.setFillStyle(0x0f172a, 0.92));
    bg.on("pointerdown",  callback);

    return { bg, txt };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 30 level nodes — themed circles + glowing connector lines
  // ─────────────────────────────────────────────────────────────────────────
  _drawNodes(W, H) {
    const t       = this.theme;
    const primary = hexToPhaser(t.primary);
    const gold    = hexToPhaser(t.secondary);
    const muted   = 0x2d3748;

    const TOP     = 82;
    const SPACING = 80;
    const COLS    = 3;

    // Usar helper do LevelStore — sem acesso directo a campos internos
    const unlockedLevel = LevelStore.getUnlockedLevel(this.regionId);

    const container = this.add.container(0, 0).setDepth(20);

    for (let i = 1; i <= 30; i++) {
      const { x, y } = this._nodePos(i, W, TOP, SPACING, COLS);
      const locked    = i > unlockedLevel;
      const stars     = LevelStore.getStars(i, this.regionId); // chave composta
      const completed = stars > 0;
      const isCurrent = i === unlockedLevel;

      // ── Connector line ─────────────────────────────────────────────────
      if (i > 1) {
        const prev        = this._nodePos(i - 1, W, TOP, SPACING, COLS);
        const segUnlocked = (i - 1) < unlockedLevel;
        const lineColor   = segUnlocked ? primary : muted;
        const lineAlpha   = segUnlocked ? 0.65    : 0.35;

        const g = this.add.graphics();

        g.lineStyle(6, lineColor, segUnlocked ? 0.18 : 0.12);
        g.beginPath(); g.moveTo(prev.x, prev.y); g.lineTo(x, y); g.strokePath();

        g.lineStyle(2, lineColor, lineAlpha);
        g.beginPath(); g.moveTo(prev.x, prev.y); g.lineTo(x, y); g.strokePath();

        if (segUnlocked) {
          this.tweens.add({
            targets:  g,
            alpha:    { from: 1, to: 0.40 },
            duration: Phaser.Math.Between(1800, 3200),
            yoyo:     true,
            repeat:   -1,
            ease:     "Sine.InOut",
            delay:    Phaser.Math.Between(0, 2000),
          });
        }
        container.add(g);
      }

      // ── Circle ─────────────────────────────────────────────────────────
      const r           = locked ? 24 : (completed ? 27 : 29);
      const fillColor   = locked ? 0x0c1018 : (completed ? 0x1c0f04 : 0x1a0a06);
      const strokeColor = locked ? muted : (completed ? gold : primary);
      const strokeAlpha = locked ? 0.45 : 0.95;
      const strokeW     = locked ? 2 : 2.5;

      const node = this.add.circle(x, y, r, fillColor, 1)
        .setStrokeStyle(strokeW, strokeColor, strokeAlpha)
        .setDepth(21)
        .setInteractive({ useHandCursor: true });

      if (isCurrent) {
        this.tweens.add({
          targets:  node,
          scaleX:   1.16,
          scaleY:   1.16,
          duration: 950,
          yoyo:     true,
          repeat:   -1,
          ease:     "Sine.InOut",
        });
      }

      const label = this.add.text(x, y - 1, String(i), {
        fontFamily: "system-ui, Arial",
        fontSize:   locked ? "11px" : "13px",
        color:      locked ? "#3d4f66" : (completed ? t.secondary : "#f1f5f9"),
        fontStyle:  locked ? "normal" : "bold",
      }).setOrigin(0.5).setDepth(22);

      if (stars > 0) {
        this.add.text(x, y + r + 4, "★".repeat(stars), {
          fontFamily: "system-ui, Arial",
          fontSize:   "18px",
          color:      t.secondary,
        }).setOrigin(0.5, 0).setDepth(22);
      }

      node.on("pointerover", () => {
        if (!locked) node.setStrokeStyle(strokeW + 1.5, strokeColor, 1);
      });
      node.on("pointerout", () => {
        node.setStrokeStyle(strokeW, strokeColor, strokeAlpha);
      });
      node.on("pointerdown", () => {
        if (!locked) {
          this.scene.start("LevelScene",{ regionId: this.regionId, levelId: i });
        } else {
          this.tweens.add({
            targets: node, x: x + 4, duration: 45,
            yoyo: true, repeat: 3,
            onComplete: () => node.setPosition(x, y),
          });
        }
      });

      container.add([node, label]);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Bottom bar — prev-region | region info | next-region
  // ─────────────────────────────────────────────────────────────────────────
  _drawBottomBar(W, H) {
    const t      = this.theme;
    const D      = 50;
    const barH   = 92;
    const barY   = H - barH / 2;
    const meta   = REGION_META[this.regionId] || REGION_META.volcano;

    this.add.rectangle(W / 2, barY, W, barH, 0x08090f, 0.93)
      .setDepth(D);

    this.add.rectangle(0, H - barH, W, 2, hexToPhaser(t.primary), 0.50)
      .setOrigin(0, 0)
      .setDepth(D + 1);

    // Prev-region button (left) — visible when a previous region exists
    const regionIdx    = REGIONS_ORDER.indexOf(this.regionId);
    const prevRegionId = regionIdx > 0 ? REGIONS_ORDER[regionIdx - 1] : null;

    if (prevRegionId) {
      const prevName  = RegionThemes[prevRegionId]?.name || prevRegionId;
      const prevColor = RegionThemes[prevRegionId]?.primary || "#94a3b8";
      this._makeButton(58, barY, `← ${prevName}`, prevColor, 100, D + 2,
        () => this.scene.start("MapScene", { regionId: prevRegionId }));
    } else {
      // First region — decorative monster icon, no navigation
      this.add.image(52, barY - 4, "map-fire")
        .setDisplaySize(62, 62)
        .setDepth(D + 2);

      this.add.text(52, barY + 32, meta.monster, {
        fontFamily: "system-ui, Arial",
        fontSize:   "10px",
        color:      t.primary,
        fontStyle:  "bold",
      }).setOrigin(0.5).setDepth(D + 2);
    }

    // Region info (centre)
    const regionLabel = I18n.t(meta.labelKey, { n: meta.labelN });
    this.add.text(W / 2, barY - 14, t.name || I18n.t('region_unknown'), {
      fontFamily: "system-ui, Arial",
      fontSize:   "14px",
      color:      t.starColor,
      fontStyle:  "bold",
    }).setOrigin(0.5).setDepth(D + 2);

    this.add.text(W / 2, barY + 6, I18n.t('region_info', { label: regionLabel }), {
      fontFamily: "system-ui, Arial",
      fontSize:   "10px",
      color:      "#4b6080",
    }).setOrigin(0.5).setDepth(D + 2);

    // Next world button (right)
    const nextRegionId = REGIONS_ORDER[REGIONS_ORDER.indexOf(this.regionId) + 1] || null;
    const regionDone   = LevelStore.getUnlockedLevel(this.regionId) > 30;
    const hasNext      = !!meta.nextRegion;

    if (!hasNext) {
      // Última região — botão Modo Infinito se desbloqueado, senão placeholder
      if (LevelStore.isInfiniteUnlocked()) {
        const best = LevelStore.getInfiniteBestScore();
        this._makeButton(W - 58, barY - 10, I18n.t('btn_infinite'), "#d084fc", 100, D + 2,
          () => this.scene.start("LevelScene",{ infinite: true }));
        if (best > 0) {
          this.add.text(W - 58, barY + 16, I18n.t('best', { n: best.toLocaleString() }), {
            fontFamily: "system-ui, Arial",
            fontSize:   "8px",
            color:      "#9333ea",
          }).setOrigin(0.5).setDepth(D + 2);
        }
      } else {
        this.add.text(W - 58, barY, I18n.t('end_of_world'), {
          fontFamily: "system-ui, Arial",
          fontSize:   "10px",
          color:      "#475569",
          align:      "center",
        }).setOrigin(0.5).setDepth(D + 2);
      }
    } else {
      const nextMeta  = REGION_META[meta.nextRegion];
      const nextName  = nextMeta ? RegionThemes[meta.nextRegion]?.name || meta.nextRegion : '';
      const btnColor  = regionDone ? t.secondary : "#475569";
      const btnLabel  = regionDone ? `${nextName}  →` : `🔒 ${nextName}`;

      this._makeButton(W - 58, barY, btnLabel, btnColor, 100, D + 2, () => {
        // Payment gate — all regions beyond volcano require full-game unlock
        if (!PaymentService.isUnlocked()) {
          this.scene.start("StoreScene", { returnTo: "MapScene", regionId: this.regionId });
          return;
        }
        if (regionDone && nextRegionId) {
          if (LevelStore.isRegionUnlocked(nextRegionId)) {
            this.scene.start("MapScene", { regionId: nextRegionId });
          } else {
            this._showToast(I18n.t('region_locked_toast'));
          }
        } else {
          const left = Math.max(0, 31 - LevelStore.getUnlockedLevel(this.regionId));
          this._showToast(I18n.t('levels_remaining', { n: left }));
        }
      });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Modals
  // ─────────────────────────────────────────────────────────────────────────
  _showBestiaryModal() {
    this.scene.start("BestiaryScene", { regionId: this.regionId || "volcano" });
  }

  _showToast(msg) {
    const W     = this.scale.width;
    const toast = this.add.text(W / 2, 110, msg, {
      fontFamily:      "system-ui, Arial",
      fontSize:        "13px",
      color:           "#f1f5f9",
      backgroundColor: "#1a2540",
      padding:         { x: 14, y: 8 },
    }).setOrigin(0.5).setAlpha(0).setDepth(300);

    this.tweens.add({
      targets:  toast,
      alpha:    1,
      duration: 180,
      yoyo:     true,
      hold:     1800,
      ease:     "Sine.InOut",
      onComplete: () => toast.destroy(),
    });
  }

  _openModal(title, body, accentColor = this.theme?.primary || "#ff4500") {
    const W       = this.scale.width;
    const H       = this.scale.height;
    const D       = 500;
    const accent  = hexToPhaser(accentColor);

    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.78)
      .setDepth(D).setInteractive();

    const panel = this.add.rectangle(W / 2, H / 2, W * 0.82, 230, 0x0c1018, 0.97)
      .setStrokeStyle(2, accent, 0.80)
      .setDepth(D + 1);

    const panelL = W / 2 - (W * 0.82) / 2;
    this.add.rectangle(panelL, H / 2 - 115, W * 0.82, 3, accent, 0.7)
      .setOrigin(0, 0).setDepth(D + 2);

    const titleTxt = this.add.text(W / 2, H / 2 - 72, title, {
      fontFamily: "system-ui, Arial",
      fontSize:   "20px",
      color:      accentColor,
      fontStyle:  "bold",
    }).setOrigin(0.5).setDepth(D + 2);

    const bodyTxt = this.add.text(W / 2, H / 2 - 8, body, {
      fontFamily: "system-ui, Arial",
      fontSize:   "14px",
      color:      "#94a3b8",
      align:      "center",
      lineSpacing: 6,
    }).setOrigin(0.5).setDepth(D + 2);

    const closeBtn = this.add.text(W / 2, H / 2 + 80, I18n.t('btn_close'), {
      fontFamily: "system-ui, Arial",
      fontSize:   "13px",
      color:      accentColor,
      fontStyle:  "bold",
    }).setOrigin(0.5).setDepth(D + 2).setInteractive({ useHandCursor: true });

    const all      = [overlay, panel, titleTxt, bodyTxt, closeBtn];
    const closeAll = () => all.forEach(o => o.destroy());

    overlay.on("pointerdown", closeAll);
    closeBtn.on("pointerdown", closeAll);
    closeBtn.on("pointerover",  () => closeBtn.setColor("#ffffff"));
    closeBtn.on("pointerout",   () => closeBtn.setColor(accentColor));
  }

  // ─────────────────────────────────────────────────────────────────────────
  // [DEV] Panel — seleciona qualquer região/nível diretamente
  // Ativado via tecla D ou botão DEV (só em import.meta.env.DEV)
  // ─────────────────────────────────────────────────────────────────────────
  _openDevPanel() {
    const W = this.scale.width;
    const H = this.scale.height;
    const D = 600;

    let activeRegion = this.regionId;

    // ── Overlay + painel ──────────────────────────────────────────────────
    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.88)
      .setDepth(D).setInteractive();

    const panelW  = W * 0.92;
    const panelH  = 360;
    const panelY  = H / 2;
    const panelTop = panelY - panelH / 2;

    const panel = this.add.rectangle(W / 2, panelY, panelW, panelH, 0x080c18, 0.98)
      .setStrokeStyle(2, 0xa855f7, 0.9)
      .setDepth(D + 1);

    const titleTxt = this.add.text(W / 2, panelTop + 22, '🛠  DEV MODE', {
      fontFamily: 'system-ui, Arial',
      fontSize:   '15px',
      color:      '#a855f7',
      fontStyle:  'bold',
    }).setOrigin(0.5).setDepth(D + 2);

    const hintTxt = this.add.text(W / 2, panelTop + 40, 'todos os níveis acessíveis  •  tecla D  •  ESC fecha', {
      fontFamily: 'system-ui, Arial',
      fontSize:   '8px',
      color:      '#4b5563',
    }).setOrigin(0.5).setDepth(D + 2);

    const closeBtn = this.add.text(W / 2, panelTop + panelH - 20, 'FECHAR  ✕', {
      fontFamily: 'system-ui, Arial',
      fontSize:   '11px',
      color:      '#a855f7',
      fontStyle:  'bold',
    }).setOrigin(0.5).setDepth(D + 2).setInteractive({ useHandCursor: true });

    const fixedItems = [overlay, panel, titleTxt, hintTxt, closeBtn];

    // ── Tabs de região ─────────────────────────────────────────────────────
    const REGIONS     = ['volcano', 'storm', 'ocean', 'earth', 'psychic'];
    const REGION_ABBR = ['🌋 VOL', '⚡ TEM', '🌊 OCN', '🌿 TER', '🔮 PSI'];
    const tabY = panelTop + 62;
    const tabW = (panelW - 20) / REGIONS.length - 4;
    const tabBtns = [];

    REGIONS.forEach((rId, idx) => {
      const tx       = W / 2 - panelW / 2 + 10 + (tabW + 4) * idx + tabW / 2;
      const isActive = rId === activeRegion;

      const tabBg = this.add.rectangle(tx, tabY, tabW, 22,
        isActive ? 0x2d1b6e : 0x111827, 0.95)
        .setStrokeStyle(1, isActive ? 0xa855f7 : 0x334155, isActive ? 1 : 0.4)
        .setDepth(D + 2).setInteractive({ useHandCursor: true });

      const tabLabel = this.add.text(tx, tabY, REGION_ABBR[idx], {
        fontFamily: 'system-ui, Arial',
        fontSize:   '9px',
        color:      isActive ? '#d8b4fe' : '#64748b',
        fontStyle:  'bold',
      }).setOrigin(0.5).setDepth(D + 3);

      tabBtns.push({ bg: tabBg, label: tabLabel, rId });
      fixedItems.push(tabBg, tabLabel);

      tabBg.on('pointerdown', () => {
        if (activeRegion === rId) return;
        activeRegion = rId;

        tabBtns.forEach(({ bg, label, rId: r }) => {
          const active = r === activeRegion;
          bg.setFillStyle(active ? 0x2d1b6e : 0x111827, 0.95);
          bg.setStrokeStyle(1, active ? 0xa855f7 : 0x334155, active ? 1 : 0.4);
          label.setColor(active ? '#d8b4fe' : '#64748b');
        });

        levelItems.forEach(o => o?.destroy());
        levelItems.length = 0;
        drawLevels(activeRegion);
      });
    });

    // ── Grelha de 30 níveis ────────────────────────────────────────────────
    const levelItems = [];
    const COLS   = 6;
    const BTN_W  = 62;
    const BTN_H  = 36;
    const GAP_X  = 6;
    const GAP_Y  = 6;
    const gridW  = COLS * (BTN_W + GAP_X) - GAP_X;
    const startX = W / 2 - gridW / 2 + BTN_W / 2;
    const startY = tabY + 33;  // tab halfH(11) + gap(11) + btn halfH(11)

    const drawLevels = (regionId) => {
      for (let i = 1; i <= 30; i++) {
        const col   = (i - 1) % COLS;
        const row   = Math.floor((i - 1) / COLS);
        const bx    = startX + col * (BTN_W + GAP_X);
        const by    = startY + row * (BTN_H + GAP_Y);
        const stars = LevelStore.getStars(i, regionId);

        const btnBg = this.add.rectangle(bx, by, BTN_W, BTN_H, 0x131c2e, 0.95)
          .setStrokeStyle(1.5, 0x4f46e5, 0.7)
          .setDepth(D + 3).setInteractive({ useHandCursor: true });

        const btnNum = this.add.text(bx, by - (stars > 0 ? 6 : 0), String(i), {
          fontFamily: 'system-ui, Arial',
          fontSize:   '13px',
          color:      '#c7d2fe',
          fontStyle:  'bold',
        }).setOrigin(0.5).setDepth(D + 4);

        levelItems.push(btnBg, btnNum);

        if (stars > 0) {
          const starTxt = this.add.text(bx, by + 10, '★'.repeat(stars), {
            fontFamily: 'system-ui, Arial',
            fontSize:   '8px',
            color:      '#fbbf24',
          }).setOrigin(0.5).setDepth(D + 4);
          levelItems.push(starTxt);
        }

        btnBg.on('pointerover', () => btnBg.setFillStyle(0x312e81, 1));
        btnBg.on('pointerout',  () => btnBg.setFillStyle(0x131c2e, 0.95));
        btnBg.on('pointerdown', () => {
          this.scene.start("LevelScene",{ regionId, levelId: i });
        });
      }
    };

    drawLevels(activeRegion);

    // ── Fechar ────────────────────────────────────────────────────────────
    const escKey  = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
    const closeAll = () => {
      fixedItems.forEach(o => o?.destroy());
      levelItems.forEach(o => o?.destroy());
      try { this.input.keyboard.removeKey(Phaser.Input.Keyboard.KeyCodes.ESC); } catch (_) {}
    };

    overlay.on('pointerdown',  closeAll);
    closeBtn.on('pointerdown', closeAll);
    closeBtn.on('pointerover',  () => closeBtn.setColor('#ffffff'));
    closeBtn.on('pointerout',   () => closeBtn.setColor('#a855f7'));
    escKey.once('down', closeAll);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Shared helper — node grid position
  // ─────────────────────────────────────────────────────────────────────────
  _nodePos(i, w, top, spacing, cols) {
    const row   = Math.floor((i - 1) / cols);
    const col   = (i - 1) % cols;
    const xBase = w / 2 + (col - 1) * 110;
    const x     = xBase + (row % 2 === 0 ? 0 : (col - 1) * 18);
    const y     = top + row * spacing;
    return { x, y };
  }
}
